import { useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Edit, Mail, MoreHorizontal, Trash2, User } from 'lucide-react'
import { DeleteUser } from '../components/DeleteUser'
import { UserForm } from '../components/UserForm'
import { UserCard } from '../components/UserCard'
import { roles as allRoles, roles } from '../constants/roles'
import { useUserPasswordMutation } from '../hooks'
import { useUsersStore } from '../store/usersStore'
import { UserRoleEnum } from '../types/user'
import type { UserRole } from '../types/user'
import type { userFilterSchema } from '@/schemas/filterSchemas'
import { Badge } from '@/components/custom/badge'
import { ListPageHeader } from '@/components/custom/ListPageHeader'
import { PaginationWrapper } from '@/components/custom/PaginationWrapper'
import { TBody, TD, TH, THead, TR, Table } from '@/components/custom/table'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useAuthStore } from '@/features/auth/store/authStore'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { useCleanQueryStates, usePagination } from '@/hooks'

export default function Users() {
  const navigate = useNavigate({ from: '/users' })
  const users = useUsersStore((s) => s.users)
  const { user: authUser } = useAuthStore()
  const churches = useChurchesStore((s) => s.churches)

  const [query, setQuery] =
    useCleanQueryStates<typeof userFilterSchema>('/authed/users')

  const [open, setOpen] = useState({ add: false, edit: false, delete: false })
  const [userId, setUserId] = useState<string | null>(null)
  const [sendingUserId, setSendingUserId] = useState<string | null>(null)

  const sendEmail = async (id: string, email: string) => {
    setSendingUserId(id)
    try {
      await sendSetupEmail(email)
    } finally {
      setSendingUserId(null)
    }
  }

  const { mutateAsync: sendSetupEmail, isPending: isSending } =
    useUserPasswordMutation()

  const filtered = useMemo(
    () =>
      users
        .filter((u) => {
          const matchesSearch =
            query.search === '' ||
            u.name.toLowerCase().includes(query.search.toLowerCase()) ||
            u.email.toLowerCase().includes(query.search.toLowerCase()) ||
            roles
              .filter((role) => u.roles.includes(role._id))
              .some((r) =>
                r.name.toLowerCase().includes(query.search.toLowerCase()),
              )

          const matchesChurch =
            query.churchId.length === 0 ||
            u.churchId?.some((id) => query.churchId.includes(id))

          const matchesRole =
            query.roles.length === 0 ||
            query.roles.some((role) => u.roles.includes(role as UserRole))

          const matchesOnlyActives =
            !query.onlyActives || u.active === query.onlyActives

          return (
            matchesSearch && matchesChurch && matchesRole && matchesOnlyActives
          )
        })
        .sort((a, b) => a.name.trim().localeCompare(b.name.trim())),
    [users, query],
  )

  const { page, pageCount, rows, setPage, goToFirstPage } = usePagination({
    data: filtered,
    pageSize: 10,
  })

  return (
    <div className="space-y-3">
      <ListPageHeader
        type="users"
        searchValue={query.search}
        onSearchChange={(value) => {
          setQuery({ search: value })
          goToFirstPage()
        }}
        initialFilters={query}
        onFilter={(filters) => {
          setQuery(filters)
          goToFirstPage()
        }}
        isFormOpen={open.add}
        onFormOpen={() => setOpen((o) => ({ ...o, add: true }))}
        onFormClose={() => setOpen((o) => ({ ...o, add: false }))}
        showAddButton={true}
      />

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-hidden rounded-md border bg-white">
        <Table>
          <THead>
            <TR>
              <TH>Name</TH>
              <TH>Email</TH>
              <TH>Roles</TH>
              <TH>Church</TH>
              <TH>Status</TH>
              <TH className="text-right">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((user) => {
              const userChurches = churches.filter((c) =>
                user.churchId?.includes(c._id),
              )
              const userRoles = allRoles.filter((r) =>
                user.roles.includes(r._id),
              )

              return (
                <TR key={user._id}>
                  <TD>{user.name}</TD>
                  <TD>{user.email}</TD>
                  <TD>
                    {userRoles
                      .map((r) =>
                        r._id === UserRoleEnum.PASTOR &&
                        user.isAdminPastor === true
                          ? `${r.name} (Admin)`
                          : r.name,
                      )
                      .sort()
                      .join(', ')}
                  </TD>
                  <TD>
                    {userChurches.map((c, index) => (
                      <span key={c._id}>
                        {c.name}
                        {index < userChurches.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </TD>
                  <TD>
                    <Badge>{user.active ? 'Active' : 'Inactive'}</Badge>
                  </TD>
                  <TD className="text-right">
                    {user._id !== authUser?._id ? (
                      <>
                        <UserForm
                          type="edit"
                          id={user._id}
                          onClose={() => {
                            setUserId(null)
                            setOpen((o) => ({ ...o, edit: false }))
                          }}
                          open={open.edit && userId === user._id}
                          trigger={null}
                        />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {!user.isVerified && (
                              <>
                                <DropdownMenuItem
                                  disabled={
                                    sendingUserId === user._id && isSending
                                  }
                                  onClick={() =>
                                    sendEmail(user._id, user.email)
                                  }
                                >
                                  <Mail className="h-4 w-4 mr-2" />
                                  {sendingUserId === user._id && isSending
                                    ? 'Sending…'
                                    : 'Send Setup Email'}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            <DropdownMenuItem
                              onClick={() => {
                                setUserId(user._id)
                                setOpen((o) => ({ ...o, edit: true }))
                              }}
                            >
                              <Edit className="h-4 w-4 mr-2" /> Edit
                            </DropdownMenuItem>

                            <DropdownMenuItem
                              variant="destructive"
                              onClick={() => {
                                setUserId(user._id)
                                setOpen((o) => ({ ...o, delete: true }))
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigate({ to: '/profile' })
                        }}
                      >
                        <User className="h-4 w-4" /> View User Profile
                      </Button>
                    )}
                  </TD>
                </TR>
              )
            })}
            {rows.length === 0 && (
              <TR>
                <TD colSpan={6} className="p-6 text-center text-neutral-500">
                  No results
                </TD>
              </TR>
            )}
          </TBody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <ScrollArea className="lg:hidden h-[calc(100vh-170px)]">
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-lg border p-6 text-center text-neutral-500">
              No users found
            </div>
          ) : (
            filtered.map((user) => (
              <UserCard
                key={user._id}
                user={user}
                churches={churches}
                isCurrentUser={user._id === authUser?._id}
                onEdit={(id) => {
                  setUserId(id)
                  setOpen((o) => ({ ...o, edit: true }))
                }}
                onDelete={(id) => {
                  setUserId(id)
                  setOpen((o) => ({ ...o, delete: true }))
                }}
                isSending={sendingUserId === user._id && isSending}
                sendSetupEmail={async (email: string) => {
                  setSendingUserId(user._id)
                  try {
                    await sendSetupEmail(email)
                  } finally {
                    setSendingUserId(null)
                  }
                }}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Desktop Pagination */}
      <div className="hidden lg:block">
        <PaginationWrapper
          pageCount={pageCount}
          currentPage={page}
          onPageChange={setPage}
        />
      </div>

      <DeleteUser
        id={userId}
        open={open.delete}
        onClose={() => {
          setUserId(null)
          setOpen((o) => ({ ...o, delete: false }))
        }}
      />
    </div>
  )
}
